import { BillingGroup, BillingGroupTypeEnum } from "@clockwise/schema";

// libraries
import * as React from "react";
import { Button, Container, makeStyles } from "@material-ui/core";
import { InfoOutlined, PersonAdd } from "@material-ui/icons";

import {
  MemberPerson,
  SortDirection,
  SortType,
  BillingGroupRoleEnum,
} from "./billing-groups-members.type";
import { Callout } from "../../callout";
import { Title } from "../title";
import { SearchField } from "./search-field";
import { MembersTable } from "./members-table";
import { MultiFilter } from "./multi-filter";

import { getValue } from "@util/errorable.util";

import { colors } from "@styles/color.styles";

import { paths } from "@clockwise/client-commons/src/constants/site";

import {
  getSubscriptionFromBillingGroup,
  isSubscriptionProductTypePro,
  isSubscriptionNonStandard,
  getPersonListCountFromBillingGroup,
} from "./billing-groups-members.util";

export interface MembersProps {
  value: string;
  persons: MemberPerson[];
  billingGroups: BillingGroup[];
  sendingInvite: string;
  sortType: SortType;
  sortDirection: SortDirection;
  previousPersonId: string | null;
  nextPersonId: string | null;
  sendInvite: (email: string) => void;
  onSort: (sortType: SortType) => void;
  onSearch: (query: string) => void;
  onMultiFilterSelect: (teamId: string, billingGroupId: string) => void;
  onPaginationAction: (pageToken: string | null, fetchNext: boolean) => void;
  teamOptions: { label: string; value: string }[];
  billingGroupOptions: { label: string; value: string }[];
  onAdminSelectChange: (
    userId: string,
    billingGroupId: string,
    role: BillingGroupRoleEnum,
    personId: string,
    errorMsg?: string,
  ) => void;
  onRemoveMember: (personIds: string[], billingGroupId: string, errorMsg?: string) => void;
  onAddMember: (personIds: string[], billingGroupId: string) => void;
  onRequestToJoinMember: (personId: string, billingGroupId: string, email: string) => void;
  billingGroupIdFilter?: string;
  teamIdFilter?: string;
  onSetInviteDialogOpen: (open: boolean) => void;
  selectedMembers: MemberPerson[];
  onSelectOneMember: (person: MemberPerson) => void;
  onSelectAllMembers: () => void;
  isSelectAllChecked: boolean;
  isSelectDisabled: boolean;
}

const useStyles = makeStyles({
  container: {
    "& > :last-child": {
      margin: "5px 0 20px 0",
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  callout: {
    fontSize: 14,
    lineHeight: "20px",
    padding: 10,
    height: 45,
    marginBottom: 30,
  },
  bold: {
    fontWeight: "bold",
  },
  infoIcon: {
    color: colors.blueCool.standard,
  },
  manageTeamButton: {
    color: colors.blueDusk.lighter,
    marginLeft: "auto",
  },
});

export const BillingGroupsMembers = ({
  value,
  persons,
  billingGroups,
  onSearch,
  onMultiFilterSelect,
  ...props
}: MembersProps) => {
  const classes = useStyles();

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const onClickInvite = () => {
    props.onSetInviteDialogOpen(true);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  const maybeRenderBillingGroupCallout = () => {
    const adminBillingGroups = billingGroups.filter((bg) => {
      return (
        bg.adminOrgPersons.__typename === "OrgPersonList" &&
        bg.adminOrgPersons.list.findIndex((a) => a.isYou) >= 0
      );
    });

    const userBillingGroups = billingGroups.filter((bg) => {
      return (
        bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
        bg.manualMembers.list.findIndex(
          (a) => a.orgPerson?.__typename === "OrgPerson" && a.orgPerson.isYou,
        ) >= 0
      );
    });

    const ssoConfigCalloutBillingGroups = adminBillingGroups.filter((bg) => {
      const dsyncConfigured = getValue(bg.dsyncConfigured);
      return (
        bg.type === BillingGroupTypeEnum.Sso &&
        dsyncConfigured &&
        !dsyncConfigured.isDsyncConfigured
      );
    });

    if (ssoConfigCalloutBillingGroups.length > 0) {
      return maybeRenderBillingGroupSsoConfigCallout(ssoConfigCalloutBillingGroups[0]);
    } else if (adminBillingGroups.length > 0) {
      return maybeRenderBillingGroupAdminCallout(adminBillingGroups[0]);
    } else if (userBillingGroups.length > 0) {
      return maybeRenderBillingGroupMemberCallout(userBillingGroups[0]);
    }

    return "";
  };

  const maybeRenderBillingGroupSsoConfigCallout = (bg: BillingGroup) => {
    return (
      <Callout variant="box" level="info" className={classes.callout}>
        <div className={classes.row}>
          <InfoOutlined className={classes.infoIcon} />
          <div style={{ marginLeft: 5 }}>
            You're an administrator of <span className={classes.bold}>{bg.name}</span>. Please{" "}
            configure SCIM to manage members in your subscription.
          </div>
          <Button
            variant="text"
            classes={{ root: classes.manageTeamButton }}
            href={`${paths.admin}/billing`}
          >
            CONFIGURE SCIM
          </Button>
        </div>
      </Callout>
    );
  };

  const maybeRenderBillingGroupAdminCallout = (bg: BillingGroup) => {
    if (!bg) {
      return;
    }
    const seatCount =
      bg.subscription.__typename === "SubscriptionDetails"
        ? bg.subscription.subscription.seatCount
        : 0;

    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);

    const personListCount = getPersonListCountFromBillingGroup(bg) || 0;

    const isNonStandardPlan =
      billingGroupSubscription && isSubscriptionNonStandard(billingGroupSubscription.subscription);

    return (
      <Callout variant="box" level="info" className={classes.callout}>
        <div className={classes.row}>
          <InfoOutlined className={classes.infoIcon} />
          <div style={{ marginLeft: 5 }}>
            You're an administrator of <span className={classes.bold}>{bg.name}</span>
            {!isNonStandardPlan && (
              <>
                {" "}
                with <span className={classes.bold}>{seatCount}</span> paid seat
                {seatCount === 1 ? "" : "s"}
              </>
            )}
            . Currently, <span className={classes.bold}>{personListCount}</span> paid seat
            {personListCount === 1 ? " is " : "s are "} being used.
          </div>
          <Button
            variant="text"
            classes={{ root: classes.manageTeamButton }}
            href={`${paths.admin}/billing`}
          >
            Manage plan
          </Button>
        </div>
      </Callout>
    );
  };

  const maybeRenderBillingGroupMemberCallout = (bg: BillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);

    const isProductTypePro =
      billingGroupSubscription &&
      isSubscriptionProductTypePro(billingGroupSubscription.subscription);

    const renderSubscriptionName = () => {
      return isProductTypePro ? (
        <>
          {" "}
          You're on the subscription <span className={classes.bold}>{bg.name}</span>{" "}
        </>
      ) : (
        <>
          {" "}
          You're on the subscription <span className={classes.bold}>{bg.name}</span>, contact your
          admin to manage users in your subscription.{" "}
        </>
      );
    };
    return (
      <Callout variant="box" level="info" className={classes.callout}>
        <div className={classes.row}>
          <InfoOutlined className={classes.infoIcon} />
          <div style={{ marginLeft: 5 }}>{renderSubscriptionName()}</div>
          <Button
            variant="text"
            classes={{ root: classes.manageTeamButton }}
            href={`${paths.admin}/billing`}
          >
            Manage plan
          </Button>
        </div>
      </Callout>
    );
  };

  return (
    <Container className={classes.container} maxWidth={false}>
      <div
        className={classes.row}
        style={{ alignItems: "baseline", justifyContent: "space-between" }}
      >
        <Title>Members</Title>
        <Button
          variant="contained"
          color="primary"
          onClick={onClickInvite}
          startIcon={<PersonAdd />}
          style={{ height: 40, width: 145 }}
        >
          Invite
        </Button>
      </div>
      {maybeRenderBillingGroupCallout()}
      <div className={classes.row} style={{ justifyContent: "space-between" }}>
        <MultiFilter
          onSelect={onMultiFilterSelect}
          teamOptions={props.teamOptions}
          billingGroupOptions={props.billingGroupOptions}
          billingGroupId={props.billingGroupIdFilter}
          teamId={props.teamIdFilter}
        />
        <SearchField value={value} onSearch={onSearch} />
      </div>
      <MembersTable persons={persons} billingGroups={billingGroups} {...props} />
    </Container>
  );
};
